import { CloudHttpClient, type CloudHttpOptions } from "./cloud-http.ts";
import { OrniTransportError } from "./transport.ts";
import { randomUuid } from "./ids.ts";

export const REMOTE_TERMINAL_OBSERVE_CAPABILITY_ID = "terminal:observe" as const;
export const REMOTE_TERMINAL_CONTROL_CAPABILITY_ID = "terminal:control" as const;
export const REMOTE_TERMINAL_MAX_TICKET_TTL_SECONDS = 300;

export interface RemoteTerminalObserveCapability {
  readonly id: typeof REMOTE_TERMINAL_OBSERVE_CAPABILITY_ID | typeof REMOTE_TERMINAL_CONTROL_CAPABILITY_ID;
  readonly access: "observe" | "control";
  readonly contentRetention: "no_store";
}

/** This SDK slice never grants control and never permits transcript retention. */
export const REMOTE_TERMINAL_OBSERVE_ONLY: RemoteTerminalObserveCapability = Object.freeze({
  id: REMOTE_TERMINAL_OBSERVE_CAPABILITY_ID,
  access: "observe",
  contentRetention: "no_store",
});

export interface IssueRemoteTerminalTicketInput {
  deviceId: string;
  sessionId: string;
  processAttemptId: string;
  generation: number;
  ttlSeconds?: number;
  operationId?: string;
  access?: "observe" | "control";
  /** Opt into the Cloudflare relay; omitted keeps the existing Cloud path. */
  relay?: boolean;
}

export interface RemoteTerminalRelayPolicy {
  contentRetention: "no_store";
  maxFrameBytes: number;
  maxBufferedFrames: number;
  maxBufferedBytes: number;
}

export interface RemoteTerminalTicketClaims {
  ticketId: string;
  deviceId: string;
  sessionId: string;
  processAttemptId: string;
  generation: number;
  access: "observe" | "control";
  issuedAt: string;
  expiresAt: string;
  relayPolicy: RemoteTerminalRelayPolicy;
}

export interface IssuedRemoteTerminalTicket {
  /** Ephemeral bearer. Callers must not persist, log, or put it in navigation state. */
  ticket: string;
  claims: RemoteTerminalTicketClaims;
  created: boolean;
  capability: RemoteTerminalObserveCapability;
  relay?: NativeRelayClientDescriptor;
}

export interface NativeRelayClientDescriptor {
  routeId: string;
  epoch: number;
  expiresAt: number;
  host: string;
  relayUrl: string;
  clientToken: string;
}

export interface EnsureRemoteTerminalRelayInput {
  deviceId: string;
  operationId: string;
}

export interface RemoteTerminalTicketMutationResult {
  ticketId: string;
  changed: boolean;
}

export interface RemoteTerminalTicketClientOptions extends CloudHttpOptions {
  operationId?: () => string;
}

/** Issues and revokes ephemeral, observe-only remote terminal tickets. */
export class RemoteTerminalTicketClient {
  private readonly http: CloudHttpClient;
  private readonly operationId: () => string;

  constructor(options: RemoteTerminalTicketClientOptions) {
    this.http = new CloudHttpClient(options);
    this.operationId = options.operationId ?? requiredRandomUuid;
  }

  async issue(input: IssueRemoteTerminalTicketInput): Promise<IssuedRemoteTerminalTicket> {
    const deviceId = requiredText(input.deviceId, "A device is required.");
    const sessionId = requiredText(input.sessionId, "A Session is required.");
    const processAttemptId = requiredText(
      input.processAttemptId,
      "A current process attempt is required.",
    );
    if (!Number.isSafeInteger(input.generation) || input.generation < 1) {
      throw new OrniTransportError("INVALID_ARGUMENT", "The Session generation must be a positive integer.");
    }
    const ttlSeconds = input.ttlSeconds ?? 60;
    if (!Number.isSafeInteger(ttlSeconds)
      || ttlSeconds < 1
      || ttlSeconds > REMOTE_TERMINAL_MAX_TICKET_TTL_SECONDS) {
      throw new OrniTransportError(
        "INVALID_ARGUMENT",
        `Remote terminal tickets must expire within ${REMOTE_TERMINAL_MAX_TICKET_TTL_SECONDS} seconds.`,
      );
    }
    const value = await this.http.request(
      `/v1/devices/${encodeURIComponent(deviceId)}/sessions/${encodeURIComponent(sessionId)}/terminal-tickets`,
      {
        method: "POST",
        cache: "no-store",
        body: JSON.stringify({
          operationId: input.operationId ?? this.operationId(),
          processAttemptId,
          generation: input.generation,
          access: input.access ?? REMOTE_TERMINAL_OBSERVE_ONLY.access,
          ttlSeconds,
          ...(input.relay === true ? { relay: true } : {}),
        }),
      },
    );
    return projectIssuedTicket(value, {
      deviceId,
      sessionId,
      processAttemptId,
      generation: input.generation,
      ttlSeconds,
      access: input.access ?? "observe",
    });
  }

  async ensureDeviceRelay(input: EnsureRemoteTerminalRelayInput): Promise<NativeRelayClientDescriptor> {
    const deviceId = requiredText(input.deviceId, "A device is required.");
    const operationId = requiredText(input.operationId, "An operation id is required.");
    const value = await this.http.request(
      `/v1/devices/${encodeURIComponent(deviceId)}/remote-terminal/ensure`,
      {
        method: "POST",
        cache: "no-store",
        body: JSON.stringify({ operationId }),
      },
    );
    return projectRelay(value);
  }

  async revoke(ticketId: string): Promise<RemoteTerminalTicketMutationResult> {
    const id = requiredText(ticketId, "A remote terminal ticket is required.");
    const value = record(await this.http.request(
      `/v1/remote-terminal/tickets/${encodeURIComponent(id)}/revoke`,
      { method: "POST", cache: "no-store" },
    ));
    const returnedId = text(value.ticketId, "ticketId");
    if (returnedId !== id) throw contractError("Cloud returned a different remote terminal ticket identity.");
    return { ticketId: returnedId, changed: value.changed === true };
  }
}

function projectIssuedTicket(
  value: unknown,
  expected: Pick<RemoteTerminalTicketClaims, "deviceId" | "sessionId" | "processAttemptId" | "generation">
    & { ttlSeconds: number; access: "observe" | "control" },
): IssuedRemoteTerminalTicket {
  const root = record(value);
  const claimsValue = record(root.claims);
  const relayValue = record(claimsValue.relayPolicy);
  const claims: RemoteTerminalTicketClaims = {
    ticketId: text(claimsValue.ticketId, "claims.ticketId"),
    deviceId: text(claimsValue.deviceId, "claims.deviceId"),
    sessionId: text(claimsValue.sessionId, "claims.sessionId"),
    processAttemptId: text(claimsValue.processAttemptId, "claims.processAttemptId"),
    generation: positiveInteger(claimsValue.generation, "claims.generation"),
    access: terminalAccess(claimsValue.access, expected.access),
    issuedAt: timestamp(claimsValue.issuedAt, "claims.issuedAt"),
    expiresAt: timestamp(claimsValue.expiresAt, "claims.expiresAt"),
    relayPolicy: {
      contentRetention: noStoreRetention(relayValue.contentRetention ?? relayValue.content_retention),
      maxFrameBytes: positiveInteger(relayValue.maxFrameBytes ?? relayValue.max_frame_bytes, "claims.relayPolicy.maxFrameBytes"),
      maxBufferedFrames: positiveInteger(relayValue.maxBufferedFrames ?? relayValue.max_buffered_frames, "claims.relayPolicy.maxBufferedFrames"),
      maxBufferedBytes: positiveInteger(relayValue.maxBufferedBytes ?? relayValue.max_buffered_bytes, "claims.relayPolicy.maxBufferedBytes"),
    },
  };
  if (claims.deviceId !== expected.deviceId
    || claims.sessionId !== expected.sessionId
    || claims.processAttemptId !== expected.processAttemptId
    || claims.generation !== expected.generation) {
    throw contractError("Cloud returned a remote terminal ticket for a different Session target.");
  }
  const issuedAt = Date.parse(claims.issuedAt);
  const expiresAt = Date.parse(claims.expiresAt);
  if (expiresAt <= issuedAt
    || expiresAt - issuedAt !== expected.ttlSeconds * 1_000) {
    throw contractError("Cloud returned a remote terminal ticket with an invalid lifetime.");
  }
  return {
    ticket: text(root.ticket, "ticket"),
    claims,
    created: root.created === true,
    capability: expected.access === "control"
      ? { id: REMOTE_TERMINAL_CONTROL_CAPABILITY_ID, access: "control", contentRetention: "no_store" }
      : REMOTE_TERMINAL_OBSERVE_ONLY,
    relay: root.relay === undefined ? undefined : projectRelay(root.relay),
  };
}

function projectRelay(value: unknown): NativeRelayClientDescriptor {
  const root = record(value);
  const host = text(root.host, "relay.host").toLowerCase();
  const relayUrl = text(root.relayUrl, "relay.relayUrl");
  const endpoint = validateRelayEndpoint(relayUrl, host);
  if (typeof root.clientToken !== "string" || (endpoint !== "fixed_device" && !root.clientToken.trim())) {
    throw contractError("Cloud returned an invalid relay client credential.");
  }
  return {
    routeId: text(root.routeId, "relay.routeId"),
    epoch: positiveInteger(root.epoch, "relay.epoch"),
    expiresAt: finiteInteger(root.expiresAt, "relay.expiresAt"),
    host,
    relayUrl,
    clientToken: root.clientToken,
  };
}

function validateRelayEndpoint(value: string, expectedHost: string): "local" | "legacy" | "fixed_device" {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw contractError("Cloud returned an invalid relay URL.");
  }
  const host = parsed.hostname.toLowerCase();
  const local = host === "localhost" || host === "127.0.0.1" || host === "::1";
  if (parsed.username || parsed.password || parsed.hash) {
    throw contractError("Cloud returned a relay URL with embedded credentials or fragment data.");
  }
  if (parsed.searchParams.size > 0) {
    // Path-addressed production routes carry only the opaque 16-character
    // slug in the query. No other caller-controlled query data is accepted.
    if (parsed.searchParams.size !== 1
      || !/^[a-z0-9]{16}$/.test(parsed.searchParams.get("slug") ?? "")) {
      throw contractError("Cloud returned a relay URL with an invalid route slug.");
    }
  }
  if (local) {
    if (parsed.protocol !== "ws:" && parsed.protocol !== "wss:") {
      throw contractError("Cloud returned an invalid local relay protocol.");
    }
    return "local";
  } else {
    if (parsed.protocol !== "wss:" || parsed.port) throw contractError("Cloud returned a relay URL outside the secure Orni relay.");
  }
  const normalizedExpectedHost = expectedHost.toLowerCase();
  if (!local && !isOrniHost(normalizedExpectedHost)) {
    throw contractError("Cloud returned a relay host outside the Orni domain.");
  }
  if (!local) {
    // The URL is the value actually used to open the socket. During a
    // rolling Cloud/Worker deployment an older descriptor can briefly carry
    // its legacy `host` label alongside the new fixed-device URL. Treat that
    // metadata mismatch as a migration case only after the URL itself passes
    // the fixed-host capability checks; never broaden this to arbitrary
    // `*.orni.app` URLs.
    if (isFixedDeviceRelayHost(host)
      && parsed.pathname === "/v1/socket"
      && parsed.searchParams.size === 0
      && !parsed.hash
      && (host === normalizedExpectedHost
        || normalizedExpectedHost === "relay.orni.app"
        || isLegacyRelayHost(normalizedExpectedHost))) {
      return "fixed_device";
    }
    if (host !== "relay.orni.app") throw contractError("Cloud returned a relay URL outside relay.orni.app.");
    if (parsed.searchParams.size !== 1 || !/^[a-z0-9]{16}$/.test(parsed.searchParams.get("slug") ?? "")) {
      throw contractError("Cloud returned an invalid legacy route slug.");
    }
    const routeLabel = normalizedExpectedHost.slice(0, -".orni.app".length);
    const slug = parsed.searchParams.get("slug") ?? "";
    if (!isLegacyRelayHost(normalizedExpectedHost) || routeLabel.slice(2) !== slug) {
      throw contractError("Cloud returned a relay host that does not match its route slug.");
    }
    return "legacy";
  }
  return "local";
}

function isFixedDeviceRelayHost(value: string): boolean {
  if (!value.endsWith(".orni.app")) return false;
  return /^[a-z0-9]{18}$/.test(value.slice(0, -".orni.app".length));
}

function isLegacyRelayHost(value: string): boolean {
  if (!value.endsWith(".orni.app")) return false;
  return /^or[a-z0-9]{16}$/.test(value.slice(0, -".orni.app".length));
}

function isOrniHost(value: string): boolean {
  return value === "orni.app" || value.endsWith(".orni.app");
}

function terminalAccess(value: unknown, expected: "observe" | "control"): "observe" | "control" {
  if (value !== expected) throw contractError("Cloud returned a remote terminal ticket with an unexpected access mode.");
  return expected;
}

function noStoreRetention(value: unknown): "no_store" {
  if (value !== "no_store") throw contractError("Remote terminal relay must use no-store retention.");
  return value;
}

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw contractError("Cloud returned an invalid remote terminal ticket response.");
  }
  return value as Record<string, unknown>;
}

function text(value: unknown, path: string): string {
  if (typeof value !== "string" || !value.trim()) throw contractError(`Cloud omitted ${path}.`);
  return value;
}

function timestamp(value: unknown, path: string): string {
  const result = text(value, path);
  if (!Number.isFinite(Date.parse(result))) throw contractError(`Cloud returned an invalid ${path}.`);
  return result;
}

function positiveInteger(value: unknown, path: string): number {
  if (!Number.isSafeInteger(value) || Number(value) < 1) throw contractError(`Cloud returned an invalid ${path}.`);
  return Number(value);
}

function finiteInteger(value: unknown, path: string): number {
  if (!Number.isSafeInteger(value) || Number(value) < 1) throw contractError(`Cloud returned an invalid ${path}.`);
  return Number(value);
}

function requiredText(value: string, message: string): string {
  const result = value.trim();
  if (!result) throw new OrniTransportError("INVALID_ARGUMENT", message);
  return result;
}

function contractError(message: string): OrniTransportError {
  return new OrniTransportError("CONTRACT_INVALID", message);
}

function requiredRandomUuid(): string {
  return randomUuid();
}
